import { useEffect, useMemo, useRef, useState } from "react";
import { SourceTypeIds } from "../SourceTypeIds";
import {
  parseConnectionConfiguration,
  updateConnectionConfiguration,
} from "../source-settings/SourceSettingsConfiguration";
import type { SourceSettingsModel } from "../source-settings/SourceSettingsApiAccess";
import { SharePointSourceSettingsApi } from "./SharePointSourceSettings.Api";
import type { SharePointSourceSettingsViewModel } from "./SharePointSourceSettings.ViewModel.types";
import type {
  SharePointColumn,
  SharePointConfiguration,
  SharePointList,
  SharePointSite,
} from "./SharePointSourceSettings.types";

const NONE_OPTION = "__none";

const normalizeId = (value: unknown): string => String(value ?? "");

const getColumnChoices = (column?: SharePointColumn): string[] => {
  const choices = column?.choice?.choices;
  return Array.isArray(choices)
    ? choices.filter((choice): choice is string => Boolean(choice))
    : [];
};

export const useSharePointSourceSettingsViewModel = (
  model: SourceSettingsModel,
): SharePointSourceSettingsViewModel => {
  const editingConnectionId = model.editingConnection.id;
  const onUpdateRef = useRef(model.onUpdate);
  const sourceSettingsApiRef = useRef(model.sourceSettingsApi);
  const editingConnectionRef = useRef(model.editingConnection);

  const [sites, setSites] = useState<SharePointSite[]>([]);
  const [lists, setLists] = useState<SharePointList[]>([]);
  const [peopleColumns, setPeopleColumns] = useState<SharePointColumn[]>([]);
  const [choiceColumns, setChoiceColumns] = useState<SharePointColumn[]>([]);
  const [dateColumns, setDateColumns] = useState<SharePointColumn[]>([]);
  const [siteQuery, setSiteQuery] = useState("");
  const [isSearchingSites, setIsSearchingSites] = useState(false);
  const [isLoadingLists, setIsLoadingLists] = useState(false);
  const [isLoadingColumns, setIsLoadingColumns] = useState(false);
  const [siteSearchFailed, setSiteSearchFailed] = useState(false);
  const [hasError, setHasError] = useState(false);
  const previousProfileIdentityRef = useRef(
    `${model.editingConnection.profileId ?? ""}|${model.editingConnection.profileName ?? ""}|${model.editingConnection.isAuthorised ? "1" : "0"}`,
  );

  useEffect(() => {
    onUpdateRef.current = model.onUpdate;
  }, [model.onUpdate]);

  useEffect(() => {
    sourceSettingsApiRef.current = model.sourceSettingsApi;
  }, [model.sourceSettingsApi]);

  useEffect(() => {
    editingConnectionRef.current = model.editingConnection;
  }, [model.editingConnection]);

  const isVisible =
    model.editingConnection.sourceTypeId.trim().toLowerCase() ===
      SourceTypeIds.SharePoint && model.editingConnection.isAuthorised;

  const configuration = useMemo(
    () =>
      parseConnectionConfiguration<SharePointConfiguration>(
        model.editingConnection.configuration,
      ),
    [model.editingConnection.configuration],
  );

  useEffect(() => {
    const currentProfileIdentity = `${model.editingConnection.profileId ?? ""}|${model.editingConnection.profileName ?? ""}|${model.editingConnection.isAuthorised ? "1" : "0"}`;
    const previousProfileIdentity = previousProfileIdentityRef.current;
    previousProfileIdentityRef.current = currentProfileIdentity;

    if (!isVisible || previousProfileIdentity === currentProfileIdentity) {
      return;
    }

    setSites([]);
    setLists([]);
    setPeopleColumns([]);
    setChoiceColumns([]);
    setDateColumns([]);
    setSiteQuery("");
    setSiteSearchFailed(false);

    updateConnectionConfiguration<SharePointConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({
        ...cfg,
        site: undefined,
        list: undefined,
        meFilterColumn: undefined,
        statusFilterColumn: undefined,
        statusesToLoad: [],
        doneStatus: undefined,
        dueDateColumn: undefined,
      }),
    );
  }, [
    isVisible,
    model.editingConnection.isAuthorised,
    model.editingConnection.profileId,
    model.editingConnection.profileName,
  ]);

  const statusChoices = useMemo(
    () => getColumnChoices(configuration.statusFilterColumn),
    [configuration.statusFilterColumn],
  );

  useEffect(() => {
    const searchSites = async (): Promise<void> => {
      if (!isVisible || !model.boardId || !editingConnectionId) {
        setSites([]);
        setSiteSearchFailed(false);
        return;
      }

      const term = siteQuery.trim();
      if (term.length < 2) {
        setSites([]);
        setSiteSearchFailed(false);
        return;
      }

      const sharePointApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<SharePointSourceSettingsApi>(
          SourceTypeIds.SharePoint,
        );
      if (!sharePointApi) {
        setSites([]);
        return;
      }

      setIsSearchingSites(true);
      setSiteSearchFailed(false);
      setHasError(false);
      try {
        setSites(
          await sharePointApi.getSites(
            model.boardId,
            editingConnectionId,
            term,
          ),
        );
      } catch {
        setSites([]);
        setSiteSearchFailed(true);
        setHasError(true);
      } finally {
        setIsSearchingSites(false);
      }
    };

    const timeoutId = setTimeout(() => {
      void searchSites();
    }, 300);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isVisible, model.boardId, editingConnectionId, siteQuery]);

  useEffect(() => {
    const loadLists = async (): Promise<void> => {
      const siteId = normalizeId(configuration.site?.id);
      if (!isVisible || !model.boardId || !editingConnectionId || !siteId) {
        setLists([]);
        return;
      }

      const sharePointApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<SharePointSourceSettingsApi>(
          SourceTypeIds.SharePoint,
        );
      if (!sharePointApi) {
        setLists([]);
        return;
      }

      setIsLoadingLists(true);
      setHasError(false);
      try {
        setLists(
          await sharePointApi.getLists(
            model.boardId,
            editingConnectionId,
            siteId,
          ),
        );
      } catch {
        setLists([]);
        setHasError(true);
      } finally {
        setIsLoadingLists(false);
      }
    };

    void loadLists();
  }, [configuration.site?.id, isVisible, model.boardId, editingConnectionId]);

  useEffect(() => {
    const loadColumns = async (): Promise<void> => {
      const siteId = normalizeId(configuration.site?.id);
      const listId = normalizeId(configuration.list?.id);
      if (
        !isVisible ||
        !model.boardId ||
        !editingConnectionId ||
        !siteId ||
        !listId
      ) {
        setPeopleColumns([]);
        setChoiceColumns([]);
        setDateColumns([]);
        return;
      }

      const sharePointApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<SharePointSourceSettingsApi>(
          SourceTypeIds.SharePoint,
        );
      if (!sharePointApi) {
        setPeopleColumns([]);
        setChoiceColumns([]);
        setDateColumns([]);
        return;
      }

      setIsLoadingColumns(true);
      setHasError(false);
      try {
        const columns = await sharePointApi.getColumns(
          model.boardId,
          editingConnectionId,
          siteId,
          listId,
        );
        setPeopleColumns(columns.peopleColumns || []);
        setChoiceColumns(columns.choiceColumns || []);
        setDateColumns(columns.dateColumns || []);
      } catch {
        setPeopleColumns([]);
        setChoiceColumns([]);
        setDateColumns([]);
        setHasError(true);
      } finally {
        setIsLoadingColumns(false);
      }
    };

    void loadColumns();
  }, [
    configuration.site?.id,
    configuration.list?.id,
    isVisible,
    model.boardId,
    editingConnectionId,
  ]);

  const statusReferenceErrorText = useMemo(() => {
    if (!configuration.doneStatus || !configuration.statusesToLoad?.length) {
      return "";
    }

    return configuration.statusesToLoad.includes(configuration.doneStatus)
      ? model.t(
          "board.connections.dialog.sourceSettings.sharepoint.statusReferenceError",
        )
      : "";
  }, [configuration.doneStatus, configuration.statusesToLoad, model]);

  const selectedSiteId = normalizeId(configuration.site?.id) || NONE_OPTION;
  const selectedListId = normalizeId(configuration.list?.id) || NONE_OPTION;
  const selectedMeFilterColumnId =
    normalizeId(configuration.meFilterColumn?.id) || NONE_OPTION;
  const selectedStatusFilterColumnId =
    normalizeId(configuration.statusFilterColumn?.id) || NONE_OPTION;
  const selectedDueDateColumnId =
    normalizeId(configuration.dueDateColumn?.id) || NONE_OPTION;

  return {
    isVisible,
    isSearchingSites,
    isLoadingLists,
    isLoadingColumns,
    hasError,
    siteSearchFailed,
    sites,
    lists,
    peopleColumns,
    choiceColumns,
    dateColumns,
    selectedSiteId,
    selectedSiteDisplayName:
      configuration.site?.displayName || configuration.site?.name || "",
    selectedSiteWebUrl: configuration.site?.webUrl || "",
    selectedListId,
    selectedListDisplayName:
      configuration.list?.displayName || configuration.list?.name || "",
    selectedMeFilterColumnId,
    selectedMeFilterColumnName:
      configuration.meFilterColumn?.displayName ||
      configuration.meFilterColumn?.name ||
      "",
    selectedStatusFilterColumnId,
    selectedStatusFilterColumnName:
      configuration.statusFilterColumn?.displayName ||
      configuration.statusFilterColumn?.name ||
      "",
    statusesToLoad: configuration.statusesToLoad || [],
    doneStatus: configuration.doneStatus || "",
    selectedDueDateColumnId,
    selectedDueDateColumnName:
      configuration.dueDateColumn?.displayName ||
      configuration.dueDateColumn?.name ||
      "",
    statusChoices,
    siteQuery,
    noneOptionValue: NONE_OPTION,
    statusReferenceErrorText,
    onSiteQueryChange: (value: string) => {
      setSiteQuery(value);
    },
    onSelectSite: (siteId?: string) => {
      const selectedId = String(siteId || NONE_OPTION);
      const selectedSite = sites.find(
        (site) => normalizeId(site.id) === selectedId,
      );

      updateConnectionConfiguration<SharePointConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          site: selectedId === NONE_OPTION ? undefined : selectedSite,
          list: undefined,
          meFilterColumn: undefined,
          statusFilterColumn: undefined,
          statusesToLoad: [],
          doneStatus: undefined,
          dueDateColumn: undefined,
        }),
      );
    },
    onSelectList: (listId?: string) => {
      const selectedId = String(listId || NONE_OPTION);
      const selectedList = lists.find(
        (list) => normalizeId(list.id) === selectedId,
      );

      updateConnectionConfiguration<SharePointConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          list: selectedId === NONE_OPTION ? undefined : selectedList,
          meFilterColumn: undefined,
          statusFilterColumn: undefined,
          statusesToLoad: [],
          doneStatus: undefined,
          dueDateColumn: undefined,
        }),
      );
    },
    onSelectMeFilterColumn: (columnId?: string) => {
      const selectedId = String(columnId || NONE_OPTION);
      const selectedColumn = peopleColumns.find(
        (column) => normalizeId(column.id) === selectedId,
      );

      updateConnectionConfiguration<SharePointConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          meFilterColumn:
            selectedId === NONE_OPTION ? undefined : selectedColumn,
        }),
      );
    },
    onSelectStatusFilterColumn: (columnId?: string) => {
      const selectedId = String(columnId || NONE_OPTION);
      const selectedColumn = choiceColumns.find(
        (column) => normalizeId(column.id) === selectedId,
      );
      const selectedChoices = getColumnChoices(selectedColumn);
      const nextDoneStatus =
        selectedChoices.length > 0
          ? selectedChoices[selectedChoices.length - 1]
          : undefined;
      const nextStatusesToLoad =
        selectedChoices.length > 1
          ? selectedChoices.slice(0, selectedChoices.length - 1)
          : [];

      updateConnectionConfiguration<SharePointConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          statusFilterColumn:
            selectedId === NONE_OPTION ? undefined : selectedColumn,
          statusesToLoad: selectedId === NONE_OPTION ? [] : nextStatusesToLoad,
          doneStatus: selectedId === NONE_OPTION ? undefined : nextDoneStatus,
        }),
      );
    },
    onSelectStatusesToLoad: (statuses: string[]) => {
      updateConnectionConfiguration<SharePointConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          statusesToLoad: statuses,
        }),
      );
    },
    onSelectDoneStatus: (status?: string) => {
      const selected = String(status || NONE_OPTION);
      updateConnectionConfiguration<SharePointConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          doneStatus: selected === NONE_OPTION ? undefined : selected,
        }),
      );
    },
    onSelectDueDateColumn: (columnId?: string) => {
      const selectedId = String(columnId || NONE_OPTION);
      const selectedColumn = dateColumns.find(
        (column) => normalizeId(column.id) === selectedId,
      );

      updateConnectionConfiguration<SharePointConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          dueDateColumn:
            selectedId === NONE_OPTION ? undefined : selectedColumn,
        }),
      );
    },
  };
};
